import * as React from 'react';
import { connect } from 'react-redux';
import { Button, Form, Alert, Modal } from 'react-bootstrap';
import * as urls from '../../../shared/urls';
import fetchAuthorized from '../../../../services/fetchAuthorized';
import JsonDiff from './JsonDiff';
import moment from 'moment';
import { buildValidationSummariesForField, ValidationSummaryList, ValidationSummaryCard } from '../Components/Validation';
import { buildCopiedForm, ReferenceIntegrityError, ICopyAudit } from './ValidationRuleCopy';
import FormEdit from '../FormEdit';
import '../assets/formioadmin.css';

interface IOwnProps {
    history: any;
    match: any;
    location: any;
}

interface IPropsFromState {
    webServiceURL: string;
}

type IProps = IPropsFromState & IOwnProps;

interface IFormName {
    formName: string;
    revisionCount: number;
}

interface IFieldChange {
    formName: string;
    fieldKey: string;
    fieldLabel: string;
    componentPath: string;
    propertyPath: string;
    oldValue: string;
    newValue: string;
    oldComponentJson: string;
    newComponentJson: string;
    fromVersion: number;
    toVersion: number;
    changedDate: string;
    changedByUsername: string;
    changedByName: string;
}

interface IDiagnostics {
    revisionsScanned: number;
    revisionsParsed: number;
    revisionsParseFailed: number;
    revisionsRawTextContainsField: number;
    revisionsComponentMatched: number;
    matchedComponentPaths: string[];
}

interface IFormContainingField {
    formName: string;
    latestVersion: number;
    matchedVersions: number[];
    matchedComponentPaths: string[];
}

interface IFormValidationsForField {
    formName: string;
    versionNumber: number;
    changedDate: string;
    formJson: string;
    hasMatchingValidations: boolean;
}

interface IState {
    allForms: IFormName[];
    selectedForms: string[];
    fieldName: string;
    results: IFieldChange[];
    diagnostics: IDiagnostics | null;
    isSearching: boolean;
    hasSearched: boolean;
    errorMessage: string;
    showJsonModal: boolean;
    modalTitle: string;
    modalOldJson: string;
    modalNewJson: string;
    leftPaneWidthPct: number;
    containsFieldName: string;
    containsMostRecentOnly: boolean;
    containsResults: IFormContainingField[];
    isContainsSearching: boolean;
    hasContainsSearched: boolean;
    validationsFieldName: string;
    validationsMostRecentOnly: boolean;
    validationsIncludeAllForms: boolean;
    validationsResults: IFormValidationsForField[];
    isValidationsSearching: boolean;
    hasValidationsSearched: boolean;
    expandedValidationForms: { [index: number]: boolean };
    validationsCompareSelection: number[];
    modalCompareLeftLabel: string;
    modalCompareRightLabel: string;
    showValidationCompareModal: boolean;
    validationCompareLeft: any[];
    validationCompareRight: any[];
    compareLeftResult: IFormValidationsForField | null;
    compareRightResult: IFormValidationsForField | null;
    showCopyPreviewModal: boolean;
    copyPreviewFormJson: string;
    copyPreviewTitle: string;
    copyPreviewOverwrote: boolean;
    copyErrorMessage: string;
    copyMissingKeys: string[];
    showCommitConfirm: boolean;
    commitConfirmText: string;
    showRollbackConfirm: boolean;
    rollbackConfirmText: string;
    rollbackFormName: string;
    rollbackVersionNumber: number;
    rollbackMessage: string;
    rollbackError: string;
    isRollingBack: boolean;
}

class FormFieldHistoryController extends React.Component<IProps, IState> {
    constructor(props) {
        super(props);
        this.state = {
            allForms: [],
            selectedForms: [],
            fieldName: '',
            results: [],
            diagnostics: null,
            isSearching: false,
            hasSearched: false,
            errorMessage: '',
            showJsonModal: false,
            modalTitle: '',
            modalOldJson: '',
            modalNewJson: '',
            leftPaneWidthPct: 25,
            containsFieldName: '',
            containsMostRecentOnly: true,
            containsResults: [],
            isContainsSearching: false,
            hasContainsSearched: false,
            validationsFieldName: '',
            validationsMostRecentOnly: true,
            validationsIncludeAllForms: false,
            validationsResults: [],
            isValidationsSearching: false,
            hasValidationsSearched: false,
            expandedValidationForms: {},
            validationsCompareSelection: [],
            modalCompareLeftLabel: '',
            modalCompareRightLabel: '',
            showValidationCompareModal: false,
            validationCompareLeft: [],
            validationCompareRight: [],
            compareLeftResult: null,
            compareRightResult: null,
            showCopyPreviewModal: false,
            copyPreviewFormJson: '',
            copyPreviewTitle: '',
            copyPreviewOverwrote: false,
            copyErrorMessage: '',
            copyMissingKeys: [],
            showCommitConfirm: false,
            commitConfirmText: '',
            showRollbackConfirm: false,
            rollbackConfirmText: '',
            rollbackFormName: '',
            rollbackVersionNumber: 0,
            rollbackMessage: '',
            rollbackError: '',
            isRollingBack: false
        };
    }

    private splitRowRef = React.createRef<HTMLDivElement>();
    private isDraggingSplit = false;

    private compareLeftPaneEl: HTMLDivElement | null = null;
    private compareRightPaneEl: HTMLDivElement | null = null;
    private compareScrollLock = false;

    setCompareLeftPane = (el: HTMLDivElement | null) => {
        if (this.compareLeftPaneEl === el) return;
        if (this.compareLeftPaneEl) this.compareLeftPaneEl.removeEventListener('scroll', this.onCompareLeftScroll);
        this.compareLeftPaneEl = el;
        if (el) el.addEventListener('scroll', this.onCompareLeftScroll, { passive: true });
    };

    setCompareRightPane = (el: HTMLDivElement | null) => {
        if (this.compareRightPaneEl === el) return;
        if (this.compareRightPaneEl) this.compareRightPaneEl.removeEventListener('scroll', this.onCompareRightScroll);
        this.compareRightPaneEl = el;
        if (el) el.addEventListener('scroll', this.onCompareRightScroll, { passive: true });
    };

    onCompareLeftScroll = () => this.syncComparePaneScroll('left');
    onCompareRightScroll = () => this.syncComparePaneScroll('right');

    syncComparePaneScroll = (from: 'left' | 'right') => {
        if (this.compareScrollLock) return;
        const source = from === 'left' ? this.compareLeftPaneEl : this.compareRightPaneEl;
        const target = from === 'left' ? this.compareRightPaneEl : this.compareLeftPaneEl;
        if (!source || !target) return;

        this.compareScrollLock = true;
        target.scrollTop = source.scrollTop;
        target.scrollLeft = source.scrollLeft;
        // Release the lock on the next tick so the mirrored scroll event is ignored.
        window.setTimeout(() => {
            this.compareScrollLock = false;
        }, 0);
    };

    componentDidMount() {
        fetchAuthorized(this.props.webServiceURL + urls.GET_FORM_REVISION_NAMES_URL)
            .then((res) => res.json())
            .then((result: IFormName[]) => this.setState({ allForms: result || [] }))
            .catch(() => this.setState({ errorMessage: 'Unable to load the list of forms.' }));
    }

    onFormSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
        this.setState({ selectedForms: selected });
    };

    onFieldNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ fieldName: e.target.value });
    };

    openJsonViewer = (r: IFieldChange) => {
        this.setState({
            showJsonModal: true,
            modalTitle: `${r.formName} \u2013 ${r.fieldLabel || r.fieldKey} (v${r.fromVersion} \u2192 v${r.toVersion}) \u2013 ${moment(r.changedDate).format('MM/DD/YYYY h:mm A')}`,
            modalOldJson: r.oldComponentJson || '(field not present in this version)',
            modalNewJson: r.newComponentJson || '(field not present in this version)',
            modalCompareLeftLabel: '',
            modalCompareRightLabel: ''
        });
    };

    closeJsonViewer = () => {
        this.setState({ showJsonModal: false });
    };

    startSplitDrag = (e: React.MouseEvent) => {
        e.preventDefault();
        this.isDraggingSplit = true;
        window.addEventListener('mousemove', this.onSplitDrag);
        window.addEventListener('mouseup', this.stopSplitDrag);
    };

    onSplitDrag = (e: MouseEvent) => {
        if (!this.isDraggingSplit || !this.splitRowRef.current) return;
        const rect = this.splitRowRef.current.getBoundingClientRect();
        if (rect.width <= 0) return;
        let pct = ((e.clientX - rect.left) / rect.width) * 100;
        pct = Math.max(10, Math.min(90, pct));
        this.setState({ leftPaneWidthPct: pct });
    };

    stopSplitDrag = () => {
        this.isDraggingSplit = false;
        window.removeEventListener('mousemove', this.onSplitDrag);
        window.removeEventListener('mouseup', this.stopSplitDrag);
    };

    componentWillUnmount() {
        this.stopSplitDrag();
    }

    runSearch = () => {
        if (this.state.selectedForms.length === 0 || this.state.fieldName.trim().length === 0) {
            this.setState({ errorMessage: 'Select at least one form and enter a field name.' });
            return;
        }

        this.setState({ isSearching: true, errorMessage: '' });

        fetchAuthorized(this.props.webServiceURL + urls.SEARCH_FORM_FIELD_CHANGES_URL, {
            method: 'POST',
            body: JSON.stringify({
                formNames: this.state.selectedForms,
                fieldName: this.state.fieldName.trim()
            })
        })
            .then((res) => res.json())
            .then((result: any) => {
                this.setState({
                    results: (result && result.changes) || [],
                    diagnostics: result || null,
                    isSearching: false,
                    hasSearched: true
                });
            })
            .catch(() => {
                this.setState({ errorMessage: 'The search failed. Please try again.', isSearching: false });
            });
    };

    onContainsFieldNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ containsFieldName: e.target.value });
    };

    onContainsMostRecentOnlyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ containsMostRecentOnly: e.target.checked });
    };

    runContainsSearch = () => {
        if (this.state.containsFieldName.trim().length === 0) {
            this.setState({ errorMessage: 'Enter a field name to find the forms that contain it.' });
            return;
        }

        this.setState({ isContainsSearching: true, errorMessage: '' });

        fetchAuthorized(this.props.webServiceURL + urls.FIND_FORMS_CONTAINING_FIELD_URL, {
            method: 'POST',
            body: JSON.stringify({
                fieldName: this.state.containsFieldName.trim(),
                mostRecentOnly: this.state.containsMostRecentOnly
            })
        })
            .then((res) => res.json())
            .then((result: any) => {
                this.setState({
                    containsResults: (result && result.forms) || [],
                    isContainsSearching: false,
                    hasContainsSearched: true
                });
            })
            .catch(() => {
                this.setState({ errorMessage: 'The search failed. Please try again.', isContainsSearching: false });
            });
    };

    onValidationsFieldNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ validationsFieldName: e.target.value });
    };

    onValidationsMostRecentOnlyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ validationsMostRecentOnly: e.target.checked });
    };

    onValidationsIncludeAllFormsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ validationsIncludeAllForms: e.target.checked });
    };

    runValidationsSearch = () => {
        if (this.state.validationsFieldName.trim().length === 0) {
            this.setState({ errorMessage: 'Enter a field name to find its related validations.' });
            return;
        }

        this.setState({ isValidationsSearching: true, errorMessage: '' });

        fetchAuthorized(this.props.webServiceURL + urls.FIND_FORM_VALIDATIONS_FOR_FIELD_URL, {
            method: 'POST',
            body: JSON.stringify({
                fieldName: this.state.validationsFieldName.trim(),
                mostRecentOnly: this.state.validationsMostRecentOnly,
                includeAllForms: this.state.validationsIncludeAllForms
            })
        })
            .then((res) => res.json())
            .then((result: any) => {
                this.setState({
                    validationsResults: (result && result.forms) || [],
                    isValidationsSearching: false,
                    hasValidationsSearched: true,
                    expandedValidationForms: {},
                    validationsCompareSelection: []
                });
            })
            .catch(() => {
                this.setState({ errorMessage: 'The search failed. Please try again.', isValidationsSearching: false });
            });
    };

    getFormValidationSummaries = (f: IFormValidationsForField) => {
        try {
            const form = JSON.parse(f.formJson);
            return buildValidationSummariesForField(form, this.state.validationsFieldName.trim());
        } catch (e) {
            return null;
        }
    };

    toggleValidationForm = (index: number) => {
        this.setState((prev) => {
            const expanded = { ...prev.expandedValidationForms };
            expanded[index] = !expanded[index];
            return { expandedValidationForms: expanded };
        });
    };

    renderFormValidations = (summaries: any[] | null) => {
        if (summaries === null) {
            return <Alert variant="warning">Unable to parse the stored form definition.</Alert>;
        }

        if (summaries.length === 0) {
            return <Alert variant="info">No form-level validations reference this field.</Alert>;
        }

        return <ValidationSummaryList summaries={summaries} />;
    };

    // Renders one side of the comparison using the same card markup/badges as the regular view,
    // adding a diff-status class so added/removed/changed rules are highlighted. `side` identifies
    // which form the entries belong to so any rule can be copied to the opposite form.
    renderValidationCompareSide = (side: { summary: any; diffStatus: string }[], sideName: 'left' | 'right') => {
        if (side.length === 0) {
            return <Alert variant="info">No form-level validations reference this field.</Alert>;
        }

        const targetName = sideName === 'left' ? this.state.modalCompareRightLabel : this.state.modalCompareLeftLabel;
        const copyLabel = sideName === 'left' ? `Copy to ${targetName} \u2192` : `\u2190 Copy to ${targetName}`;

        return (
            <div className="validation-index validation-compare-index">
                {side.map((entry) => (
                    <div
                        className={`validation-compare-entry validation-compare-${entry.diffStatus}`}
                        key={`${entry.summary.target}-${entry.summary.id}`}
                    >
                        <div className="validation-compare-entry-header">
                            {entry.diffStatus !== 'unchanged' && (
                                <span className={`validation-compare-tag validation-compare-tag-${entry.diffStatus}`}>
                                    {entry.diffStatus}
                                </span>
                            )}
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="validation-compare-copy-btn"
                                onClick={() => this.copyValidationRule(sideName, entry.summary.index)}
                            >
                                {copyLabel}
                            </Button>
                        </div>
                        <ValidationSummaryCard summary={entry.summary} />
                    </div>
                ))}
            </div>
        );
    };

    toggleValidationCompareSelection = (index: number) => {
        this.setState((prev) => {
            const current = prev.validationsCompareSelection;
            if (current.indexOf(index) >= 0) {
                return { validationsCompareSelection: current.filter((i) => i !== index) };
            }
            // Keep at most the two most recently selected forms.
            const next = current.length >= 2 ? [current[current.length - 1], index] : current.concat(index);
            return { validationsCompareSelection: next };
        });
    };

    // Builds a stable signature of a validation summary used to detect added/removed/changed rules.
    getValidationSignature = (s: any) => {
        return [
            s.target || '',
            s.description || '',
            s.summary || '',
            s.message || '',
            s.type || '',
            s.disabled ? 'disabled' : '',
            (s.issues || []).map((issue: any) => `${issue.severity}:${issue.message}`).join('|')
        ].join('\u0001');
    };

    // Matches validations across two forms by their target + description so each side can be flagged
    // as unchanged, changed, added or removed while keeping the normal card formatting/badges.
    buildValidationCompareSides = (left: any[] | null, right: any[] | null) => {
        const leftSummaries = left || [];
        const rightSummaries = right || [];

        const keyOf = (s: any) => `${s.target || ''}\u0001${s.description || ''}`;
        const rightByKey: { [key: string]: any } = {};
        rightSummaries.forEach((s) => {
            rightByKey[keyOf(s)] = s;
        });
        const leftByKey: { [key: string]: any } = {};
        leftSummaries.forEach((s) => {
            leftByKey[keyOf(s)] = s;
        });

        const leftSide = leftSummaries.map((s) => {
            const counterpart = rightByKey[keyOf(s)];
            let diffStatus: string;
            if (!counterpart) {
                diffStatus = 'removed';
            } else if (this.getValidationSignature(s) !== this.getValidationSignature(counterpart)) {
                diffStatus = 'changed';
            } else {
                diffStatus = 'unchanged';
            }
            return { summary: s, diffStatus };
        });

        const rightSide = rightSummaries.map((s) => {
            const counterpart = leftByKey[keyOf(s)];
            let diffStatus: string;
            if (!counterpart) {
                diffStatus = 'added';
            } else if (this.getValidationSignature(s) !== this.getValidationSignature(counterpart)) {
                diffStatus = 'changed';
            } else {
                diffStatus = 'unchanged';
            }
            return { summary: s, diffStatus };
        });

        return { leftSide, rightSide };
    };

    closeValidationCompare = () => {
        this.setState({ showValidationCompareModal: false });
    };

    openValidationsCompare = () => {
        const [leftIndex, rightIndex] = this.state.validationsCompareSelection;
        const left = this.state.validationsResults[leftIndex];
        const right = this.state.validationsResults[rightIndex];
        if (!left || !right) {
            return;
        }

        this.setState({
            showValidationCompareModal: true,
            modalTitle: `${left.formName} (v${left.versionNumber}) \u2194 ${right.formName} (v${right.versionNumber}) \u2013 "${this.state.validationsFieldName.trim()}" validations`,
            modalCompareLeftLabel: `${left.formName} (v${left.versionNumber})`,
            modalCompareRightLabel: `${right.formName} (v${right.versionNumber})`,
            validationCompareLeft: this.getFormValidationSummaries(left) || [],
            validationCompareRight: this.getFormValidationSummaries(right) || [],
            compareLeftResult: left,
            compareRightResult: right
        });
    };

    // Copies a validation rule from one compared form into the other. `sourceSide` indicates which
    // form the rule currently lives on; the destination is the opposite form. Runs the reference
    // integrity gate, and on success opens a read-only preview of the merged form before commit.
    copyValidationRule = (sourceSide: 'left' | 'right', ruleIndex: number) => {
        const source = sourceSide === 'left' ? this.state.compareLeftResult : this.state.compareRightResult;
        const target = sourceSide === 'left' ? this.state.compareRightResult : this.state.compareLeftResult;
        if (!source || !target) {
            return;
        }

        let sourceForm: any;
        let targetForm: any;
        try {
            sourceForm = JSON.parse(source.formJson);
            targetForm = JSON.parse(target.formJson);
        } catch (error) {
            this.setState({ copyErrorMessage: 'The source or destination form JSON could not be parsed.', copyMissingKeys: [] });
            return;
        }

        const audit: ICopyAudit = {
            sourceForm: source.formName,
            sourceVersion: source.versionNumber,
            targetForm: target.formName,
            copiedByUtc: new Date().toISOString(),
            note: `Copied from ${source.formName} (v${source.versionNumber}) via Form Field History`
        };

        try {
            const { form, overwritten } = buildCopiedForm(sourceForm, ruleIndex, targetForm, audit);
            this.setState({
                showCopyPreviewModal: true,
                copyPreviewFormJson: JSON.stringify(form),
                copyPreviewTitle: `Preview: copy rule into ${target.formName} (v${target.versionNumber})`,
                copyPreviewOverwrote: overwritten,
                copyErrorMessage: '',
                copyMissingKeys: []
            });
        } catch (error) {
            if (error instanceof ReferenceIntegrityError) {
                this.setState({
                    copyErrorMessage: `This rule cannot be copied to ${target.formName} because it references components that do not exist there.`,
                    copyMissingKeys: error.missingKeys
                });
            } else {
                this.setState({
                    copyErrorMessage: error instanceof Error ? error.message : 'The rule could not be copied.',
                    copyMissingKeys: []
                });
            }
        }
    };

    closeCopyPreview = () => {
        this.setState({ showCopyPreviewModal: false });
    };

    dismissCopyError = () => {
        this.setState({ copyErrorMessage: '', copyMissingKeys: [] });
    };

    // Save handler passed to the FormEdit preview. Instead of committing immediately it opens a
    // typed-confirmation dialog (because SaveForm updates the live form for all users) and only
    // performs the actual save once the user types CONFIRM and approves.
    private pendingCommitForm: any = null;
    private pendingCommitResolve: ((formObject: any) => void) | null = null;
    private pendingCommitReject: ((reason?: any) => void) | null = null;

    commitCopiedForm = (formSchema: any) => {
        return new Promise((resolve, reject) => {
            this.pendingCommitForm = formSchema;
            this.pendingCommitResolve = resolve;
            this.pendingCommitReject = reject;
            this.setState({ showCommitConfirm: true, commitConfirmText: '' });
        });
    };

    cancelCommit = () => {
        if (this.pendingCommitReject) {
            this.pendingCommitReject(new Error('Commit cancelled by user.'));
        }
        this.pendingCommitForm = null;
        this.pendingCommitResolve = null;
        this.pendingCommitReject = null;
        this.setState({ showCommitConfirm: false, commitConfirmText: '' });
    };

    // Performs the real SaveForm POST after the user confirms. SaveForm PUTs to FormIO and records a
    // new FormRevision (with the embedded _copyAudit provenance).
    confirmCommit = () => {
        const formSchema = this.pendingCommitForm;
        const resolve = this.pendingCommitResolve;
        const reject = this.pendingCommitReject;
        this.pendingCommitForm = null;
        this.pendingCommitResolve = null;
        this.pendingCommitReject = null;
        this.setState({ showCommitConfirm: false, commitConfirmText: '' });

        fetchAuthorized(this.props.webServiceURL + urls.SAVE_FORM_URL, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(formSchema)
        })
            .then((res) => res.json())
            .then((result) => {
                if (resolve) {
                    resolve(result.formObject);
                }
            })
            .catch((error) => {
                if (reject) {
                    reject(error);
                }
            });
    };

    // Opens the typed-confirmation dialog for an append-only rollback: re-saving a prior stored
    // revision's JSON through the normal save pipeline (which creates a new revision).
    openRollbackConfirm = (formName: string, versionNumber: number) => {
        this.setState({
            showRollbackConfirm: true,
            rollbackConfirmText: '',
            rollbackFormName: formName,
            rollbackVersionNumber: versionNumber,
            rollbackError: '',
            rollbackMessage: ''
        });
    };

    cancelRollback = () => {
        this.setState({ showRollbackConfirm: false, rollbackConfirmText: '' });
    };

    confirmRollback = () => {
        const { rollbackFormName, rollbackVersionNumber } = this.state;
        this.setState({ isRollingBack: true, rollbackError: '', rollbackMessage: '' });

        fetchAuthorized(this.props.webServiceURL + urls.RESTORE_FORM_REVISION_URL, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ formName: rollbackFormName, versionNumber: rollbackVersionNumber })
        })
            .then((res) => res.json().then((body) => ({ ok: res.ok, body })))
            .then(({ ok, body }) => {
                if (!ok || !body || body.success === false) {
                    this.setState({
                        isRollingBack: false,
                        rollbackError: (body && body.message) || 'The rollback failed. Please try again.'
                    });
                    return;
                }
                this.setState({
                    isRollingBack: false,
                    showRollbackConfirm: false,
                    rollbackConfirmText: '',
                    rollbackMessage: body.message || 'The form was restored.'
                });
            })
            .catch(() => {
                this.setState({ isRollingBack: false, rollbackError: 'The rollback failed. Please try again.' });
            });
    };

    render() {
        const { allForms, results, isSearching, hasSearched, errorMessage } = this.state;
        const diag = this.state.diagnostics;

        return (
            <div style={{ padding: '15px' }}>
                <h3>Form Field Change History</h3>
                <p>
                    Select one or more forms and enter a field name (key or label) to see every change
                    made to that field across saved form revisions.
                </p>

                {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

                <Form.Group>
                    <Form.Label>Forms</Form.Label>
                    <Form.Control
                        as="select"
                        multiple
                        style={{ height: '200px' }}
                        value={this.state.selectedForms}
                        onChange={this.onFormSelectionChange}
                    >
                        {allForms.map((f) => (
                            <option key={f.formName} value={f.formName}>
                                {f.formName} ({f.revisionCount} revisions)
                            </option>
                        ))}
                    </Form.Control>
                </Form.Group>

                <Form.Group>
                    <Form.Label>Field name</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="e.g. speedLimit"
                        value={this.state.fieldName}
                        onChange={this.onFieldNameChange}
                    />
                </Form.Group>

                <Button onClick={this.runSearch} disabled={isSearching}>
                    {isSearching ? 'Searching...' : 'Search'}
                </Button>

                {hasSearched && !isSearching && (
                    <div style={{ marginTop: '20px' }}>
                        {results.length === 0 ? (
                            <Alert variant="info">
                                <div>No changes found for that field in the selected form(s).</div>
                                {diag && (
                                    <div style={{ marginTop: '10px', fontSize: '0.85em' }}>
                                        <strong>Diagnostics:</strong>
                                        <ul style={{ marginBottom: 0 }}>
                                            <li>Revisions scanned: {diag.revisionsScanned}</li>
                                            <li>Revisions parsed OK: {diag.revisionsParsed} (parse failures: {diag.revisionsParseFailed})</li>
                                            <li>Revisions whose raw JSON contains the term: {diag.revisionsRawTextContainsField}</li>
                                            <li>Revisions where a component key/label matched: {diag.revisionsComponentMatched}</li>
                                            {diag.matchedComponentPaths && diag.matchedComponentPaths.length > 0 && (
                                                <li>Matched component path(s): {diag.matchedComponentPaths.join(', ')}</li>
                                            )}
                                        </ul>
                                        {diag.revisionsRawTextContainsField > 0 && diag.revisionsComponentMatched === 0 && (
                                            <div>The term exists in the raw JSON but did not match a component key/label &mdash; it may be referenced in logic/conditions rather than being a field key.</div>
                                        )}
                                        {diag.revisionsComponentMatched > 0 && (
                                            <div>The field was found but its settings were identical across every consecutive revision.</div>
                                        )}
                                    </div>
                                )}
                            </Alert>
                        ) : (
                            <table className="table table-striped table-bordered">
                                <thead>
                                    <tr>
                                        <th>Form</th>
                                        <th>Field</th>
                                        <th>Property</th>
                                        <th>Old Value</th>
                                        <th>New Value</th>
                                        <th>Version</th>
                                        <th>Date</th>
                                        <th>Changed By</th>
                                        <th>JSON</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map((r, i) => (
                                        <tr key={i}>
                                            <td>{r.formName}</td>
                                            <td>
                                                {r.fieldLabel || r.fieldKey}
                                                <div style={{ fontSize: '0.8em', color: '#777' }}>{r.componentPath}</div>
                                            </td>
                                            <td>{r.propertyPath}</td>
                                            <td style={{ wordBreak: 'break-all' }}>{r.oldValue}</td>
                                            <td style={{ wordBreak: 'break-all' }}>{r.newValue}</td>
                                            <td>
                                                {r.fromVersion} &rarr; {r.toVersion}
                                            </td>
                                            <td>{moment(r.changedDate).format('MM/DD/YYYY h:mm A')}</td>
                                            <td>{r.changedByName || r.changedByUsername}</td>
                                            <td>
                                                <a
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        this.openJsonViewer(r);
                                                    }}
                                                >
                                                    View JSON
                                                </a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                <hr style={{ marginTop: '30px', marginBottom: '20px' }} />

                <h3>Find Forms Containing a Field</h3>
                <p>
                    Enter a field name (key or label) to list the forms that contain it &mdash; for
                    example, entering <code>EmergencyUse</code> returns every form with that field.
                </p>

                <Form.Group>
                    <Form.Label>Field name</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="e.g. EmergencyUse"
                        value={this.state.containsFieldName}
                        onChange={this.onContainsFieldNameChange}
                    />
                </Form.Group>

                <Form.Group>
                    <Form.Check
                        type="checkbox"
                        id="containsMostRecentOnly"
                        label="Search only the most recent version (uncheck to search all versions)"
                        checked={this.state.containsMostRecentOnly}
                        onChange={this.onContainsMostRecentOnlyChange}
                    />
                </Form.Group>

                <Button onClick={this.runContainsSearch} disabled={this.state.isContainsSearching}>
                    {this.state.isContainsSearching ? 'Searching...' : 'Search'}
                </Button>

                {this.state.hasContainsSearched && !this.state.isContainsSearching && (
                    <div style={{ marginTop: '20px' }}>
                        {this.state.containsResults.length === 0 ? (
                            <Alert variant="info">
                                No forms contain that field in the {this.state.containsMostRecentOnly ? 'most recent' : 'searched'} version(s).
                            </Alert>
                        ) : (
                            <table className="table table-striped table-bordered">
                                <thead>
                                    <tr>
                                        <th>Form</th>
                                        <th>Latest Version</th>
                                        <th>Found In Version(s)</th>
                                        <th>Matched Field Path(s)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.state.containsResults.map((f, i) => (
                                        <tr key={i}>
                                            <td>{f.formName}</td>
                                            <td>{f.latestVersion}</td>
                                            <td>{(f.matchedVersions || []).join(', ')}</td>
                                            <td style={{ wordBreak: 'break-all' }}>
                                                {(f.matchedComponentPaths || []).join(', ')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                <hr style={{ marginTop: '30px', marginBottom: '20px' }} />

                <h3>Find Validations Related to a Field</h3>
                <p>
                    Enter a field name (key or label) to see the form-level validations that target or
                    reference it, shown in the same format as the Form Admin validations list.
                </p>

                <Form.Group>
                    <Form.Label>Field name</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="e.g. EmergencyUse"
                        value={this.state.validationsFieldName}
                        onChange={this.onValidationsFieldNameChange}
                    />
                </Form.Group>

                <Form.Group>
                    <Form.Check
                        type="checkbox"
                        id="validationsMostRecentOnly"
                        label="Search only the most recent version (uncheck to search all versions)"
                        checked={this.state.validationsMostRecentOnly}
                        onChange={this.onValidationsMostRecentOnlyChange}
                    />
                </Form.Group>

                <Form.Group>
                    <Form.Check
                        type="checkbox"
                        id="validationsIncludeAllForms"
                        label="Show all forms (include forms with no validation referencing this field, so a rule can be copied into them)"
                        checked={this.state.validationsIncludeAllForms}
                        onChange={this.onValidationsIncludeAllFormsChange}
                    />
                </Form.Group>

                <Button onClick={this.runValidationsSearch} disabled={this.state.isValidationsSearching}>
                    {this.state.isValidationsSearching ? 'Searching...' : 'Search'}
                </Button>

                {this.state.hasValidationsSearched && !this.state.isValidationsSearching && (
                    <div style={{ marginTop: '20px' }}>
                        {this.state.rollbackMessage && (
                            <Alert variant="success" onClose={() => this.setState({ rollbackMessage: '' })} dismissible>
                                {this.state.rollbackMessage}
                            </Alert>
                        )}
                        {this.state.validationsResults.length === 0 ? (
                            <Alert variant="info">
                                No forms have validations related to that field in the {this.state.validationsMostRecentOnly ? 'most recent' : 'searched'} version(s).
                            </Alert>
                        ) : (
                            <>
                                {this.state.validationsResults.length > 1 && (
                                    <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '10px' }}>
                                        <small className="text-muted">
                                            Select two forms to compare their matching validations.
                                        </small>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={this.openValidationsCompare}
                                            disabled={this.state.validationsCompareSelection.length !== 2}
                                        >
                                            Compare selected forms ({this.state.validationsCompareSelection.length}/2)
                                        </Button>
                                    </div>
                                )}
                                {this.state.validationsResults.map((f, i) => {
                                const summaries = this.getFormValidationSummaries(f);
                                const matchCount = summaries ? summaries.length : 0;
                                const isExpanded = !!this.state.expandedValidationForms[i];
                                const isSelected = this.state.validationsCompareSelection.indexOf(i) >= 0;

                                return (
                                    <section key={i} className="card" style={{ marginBottom: '15px' }}>
                                        <div
                                            className="card-header d-flex justify-content-between align-items-center"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => this.toggleValidationForm(i)}
                                        >
                                            <span className="d-flex align-items-center">
                                                <Form.Check
                                                    type="checkbox"
                                                    id={`validationCompare_${i}`}
                                                    className="mr-2"
                                                    style={{ marginRight: '8px' }}
                                                    checked={isSelected}
                                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                    onChange={() => this.toggleValidationCompareSelection(i)}
                                                />
                                                <span style={{ marginRight: '8px' }}>{isExpanded ? '\u25bc' : '\u25b6'}</span>
                                                <strong>{f.formName}</strong>{' '}
                                                <small className="text-muted" style={{ marginLeft: '6px' }}>
                                                    {f.versionNumber > 0
                                                        ? `(v${f.versionNumber} \u2013 ${moment(f.changedDate).format('MM/DD/YYYY h:mm A')})`
                                                        : '(live form \u2013 no saved revisions)'}
                                                </small>
                                            </span>
                                            <span className="badge badge-secondary">
                                                {f.hasMatchingValidations === false
                                                    ? 'No related validations \u2013 copy target'
                                                    : `${matchCount} matching ${matchCount === 1 ? 'item' : 'items'}`}
                                            </span>
                                        </div>
                                        {isExpanded && (
                                            <div className="card-body">
                                                {f.versionNumber > 0 && (
                                                    <div className="d-flex justify-content-end" style={{ marginBottom: '10px' }}>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={(e: React.MouseEvent) => {
                                                                e.stopPropagation();
                                                                this.openRollbackConfirm(f.formName, f.versionNumber);
                                                            }}
                                                        >
                                                            Roll back {f.formName} to v{f.versionNumber}
                                                        </Button>
                                                    </div>
                                                )}
                                                {this.renderFormValidations(summaries)}
                                            </div>
                                        )}
                                    </section>
                                );
                            })}
                            </>
                        )}
                    </div>
                )}

                <Modal show={this.state.showJsonModal} onHide={this.closeJsonViewer} dialogClassName="form-field-history-json-modal">
                    <style>{`.form-field-history-json-modal { max-width: 75vw; width: 75vw; }
                        .form-field-history-json-modal .modal-content { max-height: calc(100vh - 3.5rem); }
                        .form-field-history-json-modal .modal-body { overflow: auto; }`}</style>
                    <Modal.Header closeButton>
                        <Modal.Title>{this.state.modalTitle}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div
                            style={{
                                resize: 'both',
                                overflow: 'auto',
                                height: '60vh',
                                width: '100%',
                                minHeight: '200px',
                                minWidth: '300px',
                                maxWidth: '100%',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '8px'
                            }}
                        >
                            <div style={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', flex: '0 0 auto' }}>
                                    <h6 style={{ flex: `0 0 ${this.state.leftPaneWidthPct}%`, margin: 0 }}>{this.state.modalCompareLeftLabel || 'Previous version'}</h6>
                                    <div style={{ flex: '0 0 8px' }} />
                                    <h6 style={{ flex: '1 1 0%', margin: 0 }}>{this.state.modalCompareRightLabel || 'New version'}</h6>
                                </div>
                                <div style={{ flex: 1, minHeight: 0, marginTop: '6px' }}>
                                    <JsonDiff
                                        oldJson={this.state.modalOldJson}
                                        newJson={this.state.modalNewJson}
                                        leftWidthPct={this.state.leftPaneWidthPct}
                                        onSplitterMouseDown={this.startSplitDrag}
                                        containerRef={this.splitRowRef}
                                    />
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.closeJsonViewer}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Modal show={this.state.showValidationCompareModal} onHide={this.closeValidationCompare} dialogClassName="form-field-history-compare-modal">
                    <style>{`
                        .form-field-history-compare-modal { max-width: 85vw; width: 85vw; }
                        .form-field-history-compare-modal .modal-content { max-height: calc(100vh - 3.5rem); }
                        .form-field-history-compare-modal .modal-body { overflow: auto; }
                    `}</style>
                    <Modal.Header closeButton>
                        <Modal.Title>{this.state.modalTitle}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {(() => {
                            const { leftSide, rightSide } = this.buildValidationCompareSides(
                                this.state.validationCompareLeft,
                                this.state.validationCompareRight
                            );
                            return (
                                <div className="validation-compare">
                                    <div className="validation-compare-legend">
                                        <span className="validation-compare-tag validation-compare-tag-changed">changed</span>
                                        <span className="validation-compare-tag validation-compare-tag-removed">removed</span>
                                        <span className="validation-compare-tag validation-compare-tag-added">added</span>
                                    </div>
                                    {this.state.copyErrorMessage && (
                                        <Alert variant="danger" dismissible onClose={this.dismissCopyError}>
                                            {this.state.copyErrorMessage}
                                            {this.state.copyMissingKeys.length > 0 && (
                                                <ul style={{ marginBottom: 0, marginTop: '6px' }}>
                                                    {this.state.copyMissingKeys.map((key) => (
                                                        <li key={key}>{key}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </Alert>
                                    )}
                                    <div
                                        style={{
                                            resize: 'both',
                                            overflow: 'auto',
                                            height: '60vh',
                                            width: '100%',
                                            minHeight: '200px',
                                            minWidth: '300px',
                                            maxWidth: '100%',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            padding: '8px'
                                        }}
                                    >
                                        <div style={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
                                            <div style={{ display: 'flex', flex: '0 0 auto', gap: '16px' }}>
                                                <h6 className="validation-compare-heading" style={{ flex: '1 1 0', minWidth: 0, margin: 0 }}>
                                                    {this.state.modalCompareLeftLabel}
                                                </h6>
                                                <h6 className="validation-compare-heading" style={{ flex: '1 1 0', minWidth: 0, margin: 0 }}>
                                                    {this.state.modalCompareRightLabel}
                                                </h6>
                                            </div>
                                            <div style={{ display: 'flex', flex: 1, minHeight: 0, gap: '16px', marginTop: '6px' }}>
                                                <div
                                                    ref={this.setCompareLeftPane}
                                                    style={{
                                                        flex: '1 1 0',
                                                        minWidth: 0,
                                                        height: '100%',
                                                        overflow: 'auto',
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: '4px',
                                                        padding: '0 10px'
                                                    }}
                                                >
                                                    {this.renderValidationCompareSide(leftSide, 'left')}
                                                </div>
                                                <div
                                                    ref={this.setCompareRightPane}
                                                    style={{
                                                        flex: '1 1 0',
                                                        minWidth: 0,
                                                        height: '100%',
                                                        overflow: 'auto',
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: '4px',
                                                        padding: '0 10px'
                                                    }}
                                                >
                                                    {this.renderValidationCompareSide(rightSide, 'right')}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.closeValidationCompare}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Modal
                    show={this.state.showCopyPreviewModal}
                    onHide={this.closeCopyPreview}
                    dialogClassName="form-field-history-compare-modal"
                >
                    <style>{`
                        .form-field-history-compare-modal { max-width: 85vw; width: 85vw; }
                        .form-field-history-compare-modal .modal-content { max-height: calc(100vh - 3.5rem); }
                        .form-field-history-compare-modal .modal-body { overflow: auto; }
                    `}</style>
                    <Modal.Header closeButton>
                        <Modal.Title>{this.state.copyPreviewTitle}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Alert variant={this.state.copyPreviewOverwrote ? 'warning' : 'info'}>
                            {this.state.copyPreviewOverwrote
                                ? 'A matching rule already exists in the destination form and will be overwritten. Review the merged form below, then Save to commit a new revision.'
                                : 'The rule will be added to the destination form. Review the merged form below, then Save to commit a new revision.'}
                        </Alert>
                        {this.state.showCopyPreviewModal && this.state.copyPreviewFormJson && (
                            <FormEdit
                                formJSON={this.state.copyPreviewFormJson}
                                formSave={this.commitCopiedForm}
                                cancelEdit={this.closeCopyPreview}
                                history={this.props.history}
                            />
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.closeCopyPreview}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Modal show={this.state.showCommitConfirm} onHide={this.cancelCommit} backdrop="static">
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm live form update</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Alert variant="danger">
                            Making this change will immediately update the live form for all users.
                        </Alert>
                        <p>Type <strong>CONFIRM</strong> below and click OK to approve the update.</p>
                        <Form.Control
                            type="text"
                            value={this.state.commitConfirmText}
                            onChange={(e: any) => this.setState({ commitConfirmText: e.target.value })}
                            placeholder="CONFIRM"
                            autoFocus
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.cancelCommit}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            disabled={this.state.commitConfirmText.trim() !== 'CONFIRM'}
                            onClick={this.confirmCommit}
                        >
                            OK
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Modal show={this.state.showRollbackConfirm} onHide={this.cancelRollback} backdrop="static">
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm rollback</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Alert variant="danger">
                            Making this change will immediately update the live form for all users.
                        </Alert>
                        <p>
                            This will restore <strong>{this.state.rollbackFormName}</strong> to the content of{' '}
                            <strong>v{this.state.rollbackVersionNumber}</strong> by saving a new revision.
                        </p>
                        {this.state.rollbackError && (
                            <Alert variant="danger">{this.state.rollbackError}</Alert>
                        )}
                        <p>Type <strong>CONFIRM</strong> below and click OK to approve the update.</p>
                        <Form.Control
                            type="text"
                            value={this.state.rollbackConfirmText}
                            onChange={(e: any) => this.setState({ rollbackConfirmText: e.target.value })}
                            placeholder="CONFIRM"
                            autoFocus
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.cancelRollback} disabled={this.state.isRollingBack}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            disabled={this.state.rollbackConfirmText.trim() !== 'CONFIRM' || this.state.isRollingBack}
                            onClick={this.confirmRollback}
                        >
                            {this.state.isRollingBack ? 'Rolling back\u2026' : 'OK'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

const mapStateToProps = (reduxState: any) => {
    return {
        webServiceURL: reduxState.Application.backendURL
    };
};

export default connect<IPropsFromState, {}, IOwnProps>(mapStateToProps)(FormFieldHistoryController);
